import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link2, RefreshCw, ArrowLeftRight } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

// Modal de solo lectura que muestra con qué está conciliada una factura (sus
// movimientos) o un movimiento (su factura). El propio componente renderiza el
// icono disparador 🔗 y gestiona su estado. Pide el enlace al backend al abrirse.

function fmtCurrency(amount, currency = "EUR") {
  if (amount == null) return "-";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: currency || "EUR",
  }).format(amount);
}

function fmtDate(d) {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("es-ES");
  } catch {
    return d;
  }
}

const PAY_LABELS = {
  unpaid: "Sin pagar",
  partial: "Parcial",
  paid: "Pagada",
  compensated: "Compensada",
};

export default function ReconciliationLinkDialog({ kind, id, open: openProp, onOpenChange, hideTrigger = false }) {
  const isControlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? openProp : openState;
  const setOpen = (v) => (isControlled ? onOpenChange?.(v) : setOpenState(v));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setData(null);
    try {
      const url =
        kind === "invoice"
          ? `${API}/invoices/${id}/transactions`
          : `${API}/bank/transactions/${id}/invoice`;
      const res = await apiFetch(url);
      setData(res.ok ? await res.json() : { error: true });
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpen(true);
    load();
  };

  // Modo controlado (prop `open`): cargar el enlace cuando el padre abre.
  useEffect(() => {
    if (isControlled && openProp) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProp, isControlled]);

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={handleOpen}
          title="Ver enlace de conciliación"
          className="inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100 transition-colors"
          data-testid={`link-${kind}-${id}`}
        >
          <Link2 className="w-4 h-4" />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-blue-600" />
              {kind === "invoice"
                ? "Movimientos conciliados"
                : data?.classification
                  ? "Clasificación del movimiento"
                  : "Factura conciliada"}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <RefreshCw className="w-5 h-5 animate-spin" />
            </div>
          ) : data?.error ? (
            <p className="text-sm text-rose-600 py-4">
              No se pudo cargar el enlace.
            </p>
          ) : kind === "invoice" ? (
            <InvoiceLinks transactions={data?.transactions || []} />
          ) : (
            <TransactionLink
              invoice={data?.invoice}
              type={data?.invoice_type}
              classification={data?.classification}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function InvoiceLinks({ transactions }) {
  if (transactions.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4">
        Esta factura no tiene ningún movimiento bancario conciliado todavía.
      </p>
    );
  }
  return (
    <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
      {transactions.map((t) => (
        <div
          key={t.transaction_id}
          className="rounded-lg border border-slate-200 p-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">{fmtDate(t.date)}</span>
            <span
              className={`font-mono font-semibold ${
                t.amount < 0 ? "text-rose-600" : "text-emerald-600"
              }`}
            >
              {fmtCurrency(t.amount)}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate">
            {t.description}
          </div>
        </div>
      ))}
    </div>
  );
}

function TransactionLink({ invoice, type, classification }) {
  if (!invoice && classification) {
    // Movimiento clasificado (asiento contable sin factura): enseñar la
    // clasificación en vez de "no conciliado".
    return (
      <div className="rounded-lg border border-slate-200 p-3 space-y-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-slate-900">
            {classification.category_label || classification.account_name || "Clasificado"}
          </span>
          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
            Clasificado
          </span>
        </div>
        <div className="text-xs text-slate-500">
          Cuenta {classification.account_code}
          {classification.account_name ? ` — ${classification.account_name}` : ""}
        </div>
        <p className="text-xs text-slate-500 pt-1">
          Este movimiento no corresponde a ninguna factura: se contabilizó
          directamente con un asiento de clasificación.
        </p>
      </div>
    );
  }
  if (!invoice) {
    return (
      <p className="text-sm text-slate-500 py-4">
        Este movimiento no está conciliado con ninguna factura.
      </p>
    );
  }
  const name = type === "received" ? invoice.supplier_name : invoice.client_name;
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-1">
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-900">{name || "—"}</span>
        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
          {type === "received" ? "Recibida" : "Emitida"}
        </span>
      </div>
      <div className="text-xs text-slate-500">Nº {invoice.invoice_number}</div>
      <div className="flex items-center justify-between text-sm pt-1">
        <span className="text-slate-500">{fmtDate(invoice.invoice_date)}</span>
        <span className="font-mono">
          {fmtCurrency(invoice.total_amount, invoice.currency)}
        </span>
      </div>
      <div className="text-xs text-slate-500">
        Estado: {PAY_LABELS[invoice.payment_status] || invoice.payment_status}
      </div>
    </div>
  );
}
